import Block from '../block'

/**
 * A srcdoc iframe first exposes an empty about:blank document (already in readyState
 * 'complete') which is replaced by the real document a tick later. That is why the
 * document available at construction time can never be trusted: we keep re-attaching
 * until the document actually holding the embed content is the one being observed.
 */
const ATTACH_ATTEMPTS = 20
const ATTACH_INTERVAL = 100

/* Ignore sub pixel differences, they only cause the observers to keep firing */
const HEIGHT_TOLERANCE = 2

export default class BlockEmbed extends Block {
	static title = 'block-embed'

	constructor($el) {
		super($el)

		this.iframe = this.$el.findElement('iframe').first()
		this.resizeObserver = null
		this.mutationObserver = null
		this.observedDocument = null
		this.appliedHeight = null
		this.frameRequest = null
		this.attachTimeout = null
		this.attachAttempts = 0
		this.isDestroyed = false

		this.onLoad = this.onLoad.bind(this)
		this.onResize = this.onResize.bind(this)

		this.init()
	}

	init() {
		if (this.iframe.length === 0) {
			return
		}

		/* Every (re)load hands us a new document, so always keep listening */
		this.iframe.on('load', this.onLoad)
		window.addEventListener('resize', this.onResize)

		this.attach()
	}

	/**
	 * Polls because the load event is not enough on its own: it may have fired before
	 * this component was created.
	 */
	attach() {
		this.clearAttachTimeout()

		if (this.isDestroyed) {
			return
		}

		const doc = this.getDocument()

		if (this.isReady(doc)) {
			this.observe(doc)
			return
		}

		if (this.attachAttempts >= ATTACH_ATTEMPTS) {
			return
		}

		this.attachAttempts += 1
		this.attachTimeout = window.setTimeout(() => this.attach(), ATTACH_INTERVAL)
	}

	/**
	 * The document is unreachable for cross origin embeds and for sandboxes without
	 * allow-same-origin; in that case the height stays whatever the stylesheet says.
	 */
	getDocument() {
		const iframe = this.iframe.get(0)

		if (!iframe) {
			return null
		}

		try {
			return iframe.contentDocument
		} catch (error) {
			return null
		}
	}

	isReady(doc) {
		if (!doc || !doc.body || !doc.documentElement) {
			return false
		}

		if (doc.readyState === 'loading') {
			return false
		}

		/* The about:blank placeholder is empty, the srcdoc document is not */
		return doc.body.childNodes.length > 0
	}

	observe(doc) {
		if (this.observedDocument === doc) {
			this.updateHeight()
			return
		}

		this.disconnectObservers()
		this.observedDocument = doc
		this.updateHeight()

		if (typeof ResizeObserver === 'function') {
			this.resizeObserver = new ResizeObserver(() => this.requestUpdate())
			this.resizeObserver.observe(doc.body)
			this.resizeObserver.observe(doc.documentElement)
		}

		/* Scripts inside the embed can swap content without resizing the body itself */
		if (typeof MutationObserver === 'function') {
			this.mutationObserver = new MutationObserver(() => this.requestUpdate())
			this.mutationObserver.observe(doc.body, {
				attributes: true,
				childList: true,
				subtree: true,
			})
		}

		/* Webfonts and images can still change the height after the load event */
		if (
			doc.fonts &&
			doc.fonts.ready &&
			typeof doc.fonts.ready.then === 'function'
		) {
			doc.fonts.ready.then(() => this.requestUpdate()).catch(() => {})
		}

		this.requestUpdate()
	}

	/**
	 * One write per frame keeps the resize observer from looping on the height it just
	 * caused itself.
	 */
	requestUpdate() {
		if (this.isDestroyed || this.frameRequest !== null) {
			return
		}

		this.frameRequest = window.requestAnimationFrame(() => {
			this.frameRequest = null
			this.updateHeight()
		})
	}

	updateHeight() {
		if (this.isDestroyed) {
			return
		}

		const iframe = this.iframe.get(0)

		if (!iframe) {
			return
		}

		/* The block was removed from the page, nothing left to measure */
		if (iframe.isConnected === false) {
			this.destroy()
			return
		}

		const doc = this.getDocument()

		if (!this.isReady(doc)) {
			this.attachAttempts = 0
			this.observedDocument = null
			this.attach()
			return
		}

		/* The document was replaced after we attached, observe the new one instead */
		if (doc !== this.observedDocument) {
			this.attachAttempts = 0
			this.observe(doc)
			return
		}

		const height = this.measure(doc)

		if (height === null) {
			return
		}

		if (
			this.appliedHeight !== null &&
			Math.abs(height - this.appliedHeight) < HEIGHT_TOLERANCE
		) {
			return
		}

		this.appliedHeight = height
		this.iframe.height(height)
	}

	measure(doc) {
		try {
			const view = doc.defaultView
			const style = view ? view.getComputedStyle(doc.body) : null
			const margin = style
				? parseFloat(style.marginTop) + parseFloat(style.marginBottom)
				: 0
			const offset = Number.isNaN(margin) ? 0 : margin

			let height = Math.max(
				doc.body.scrollHeight,
				doc.body.offsetHeight + offset,
				doc.body.getBoundingClientRect().height + offset,
			)

			/**
			 * The scrollHeight of the html element is clamped to the viewport height,
			 * which is the height we set ourselves, so it is only meaningful when it
			 * exceeds it: content is then overflowing the document itself. Leaving it
			 * out otherwise is what allows the iframe to shrink again.
			 */
			if (doc.documentElement.scrollHeight > doc.documentElement.clientHeight) {
				height = Math.max(height, doc.documentElement.scrollHeight)
			}

			return height > 0 ? Math.ceil(height) : null
		} catch (error) {
			/* The document became unreachable while measuring it */
			this.disconnectObservers()
			this.observedDocument = null

			return null
		}
	}

	onLoad() {
		this.attachAttempts = 0
		this.observedDocument = null

		this.attach()
	}

	onResize() {
		/* A different iframe width reflows the embed, so the height changes with it */
		this.requestUpdate()
	}

	clearAttachTimeout() {
		if (this.attachTimeout !== null) {
			window.clearTimeout(this.attachTimeout)
			this.attachTimeout = null
		}
	}

	disconnectObservers() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect()
			this.resizeObserver = null
		}

		if (this.mutationObserver) {
			this.mutationObserver.disconnect()
			this.mutationObserver = null
		}
	}

	destroy() {
		this.isDestroyed = true

		this.clearAttachTimeout()

		if (this.frameRequest !== null) {
			window.cancelAnimationFrame(this.frameRequest)
			this.frameRequest = null
		}

		this.disconnectObservers()
		this.observedDocument = null

		this.iframe.off('load', this.onLoad)
		window.removeEventListener('resize', this.onResize)

		if (typeof super.destroy === 'function') {
			super.destroy()
		}
	}
}
